import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useMediaQuery } from 'usehooks-ts';

import Config from '../../config';
import { Link } from '../link/link';
import { ImageEmbedded } from '../image/image-embedded';
import { Breakpoints, getMediaQuery } from '../../utils/breakpoints';
import { PlatformGraphicsProps, PlatformGraphicsIsEmptyFn } from './platform-graphics.config';

const PLATFORM_IMAGES = {
  en: {
    mobile: `${Config.DAM_IMAGES_PATH}SNO-Platform-mobile.png`,
    desktopClosed: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-closed.png`,
    desktopOpen: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open.png`,
  },
  ja: {
    mobile: `${Config.DAM_IMAGES_PATH}SNO-Platform-mobile-ja.png`,
    desktopClosed: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-closed-ja.png`,
    desktopOpen: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-ja.png`,
  },
  ko: {
    mobile: `${Config.DAM_IMAGES_PATH}SNO-Platform-mobile-ko.png`,
    desktopClosed: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-closed-ko.png`,
    desktopOpen: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-ko.png`,
  },
  de: {
    mobile: `${Config.DAM_IMAGES_PATH}SNO-Platform-mobile-de.png`,
    desktopClosed: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-de.png`,
    desktopOpen: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-de.png`,
  },
  es: {
    mobile: `${Config.DAM_IMAGES_PATH}SNO-Platform-mobile-es.png`,
    desktopClosed: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-es.png`,
    desktopOpen: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-es.png`,
  },
  fr: {
    mobile: `${Config.DAM_IMAGES_PATH}SNO-Platform-mobile-fr.png`,
    desktopClosed: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-fr.png`,
    desktopOpen: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-fr.png`,
  },
  it: {
    mobile: `${Config.DAM_IMAGES_PATH}SNO-Platform-mobile-it.png`,
    desktopClosed: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-closed-it.png`,
    desktopOpen: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-it.png`,
  },
  pt_br: {
    mobile: `${Config.DAM_IMAGES_PATH}SNO-Platform-mobile-pt_br.png`,
    desktopClosed: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-closed-pt_br.png`,
    desktopOpen: `${Config.DAM_IMAGES_PATH}SNO-Platform-desktop-open-pt_br.png`,
  },
};

const PlatformGraphics = ({ cta, ...props }: PlatformGraphicsProps) => {
  const [open, setOpen] = useState(false);
  const [platformGraphics, setPlatformGraphics] = useState(PLATFORM_IMAGES.en);
  const isDesktop = useMediaQuery(getMediaQuery(Breakpoints.MOBILE, 'up'));
  const { i18n } = useTranslation();

  useEffect(() => {
    setPlatformGraphics(
      PLATFORM_IMAGES[i18n.language as keyof typeof PLATFORM_IMAGES] ?? /* istanbul ignore next */ PLATFORM_IMAGES.en
    );
  }, [i18n.language]);

  const baseClassName = 'platform-graphics';

  const { buttonLink, accessibilityLabel, text, heapButtonClasses } = cta || {};

  if (PlatformGraphicsIsEmptyFn({ cta, ...props })) {
    return null;
  }

  const handleMouseOver = () => setOpen(true);

  return buttonLink ? (
    <Link
      link={buttonLink}
      ariaLabel={accessibilityLabel}
      title={text}
      dataTestid="platform-graphics-link"
      className={classNames(heapButtonClasses?.join(' '), `${baseClassName}-link`)}
    >
      <div data-testid="platform-graphics-wrapper" onMouseOver={handleMouseOver}>
        <ImageEmbedded
          baseCssClass={classNames(baseClassName, { 'platform-graphics-hide': isDesktop })}
          src={platformGraphics.mobile}
          alt="Platform Graphics Image"
          lazyEnabled
        />
        <ImageEmbedded
          baseCssClass={classNames(baseClassName, { 'platform-graphics-hide': !isDesktop || open })}
          src={platformGraphics.desktopClosed}
          alt="Platform Graphics Image"
          lazyEnabled
        />
        <ImageEmbedded
          baseCssClass={classNames(baseClassName, { 'platform-graphics-hide': !isDesktop || !open })}
          src={platformGraphics.desktopOpen}
          alt="Platform Graphics Image"
          lazyEnabled
        />
      </div>
    </Link>
  ) : null;
};

export default PlatformGraphics;
